import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetCoverageQuery } from '@/services/api/coverageService';
import {
  useApproveDataRequirementValueMutation,
  useCreateDataRequirementValueMutation,
  useGetMatterDataRequirementsQuery,
} from '@/services/api/dataRequirementsService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import type {
  AdjusterDetail,
  ContactHandleSchema,
} from '@/services/types/coverage-types';
import {
  DataRequirement,
  DataRequirementStatus,
  DataRequirementValueStatus,
} from '@/services/types/data-requirements-types';
import type { LoRScalarChoice } from '@/services/types/letters-types';
import {
  buildPreferredContactChoice,
  extractContactChannels,
} from '@/services/utils/coverageHandles';

import {
  getCoverageProgress,
  setCoverageProgress,
} from '../state/progressStore';

export type ClaimFilingStep = 'review' | 'call' | 'lor' | 'review_send' | 'ack';

export type CallStatus = 'idle' | 'in_progress' | 'success' | 'failed_terminal';

export type CoverageType = '1P' | '3P';

export interface ReviewedFieldState {
  key: string;
  displayName: string;
  required: boolean;
  value: string | null;
  isReviewed: boolean;
  /** Metadata for routing/evidence */
  meta?: {
    requirementId: string;
    taskId: string;
    section: 'client' | 'coverage';
  };
}

export interface LorArtifactMock {
  id: string;
  title: string;
  url: string;
  createdAt?: string;
}

export interface FinalizeDetails {
  policyNumber: string;
  claimNumber: string;
  adjusterName: string;
  adjusterEmail: string;
  adjusterPhone: string;
  adjusterFax: string;
  preferredContactInstructions?: string;
  adjusterContactChoice: LoRScalarChoice | null;
}

export interface UseClaimFilingStateArgs {
  matterId: string;
  coverageId: string;
}

export interface UseClaimFilingState {
  reviewedFields: ReviewedFieldState[];
  allRequiredReviewed: boolean;
  callStatus: CallStatus;
  isCallOutcomeReady: boolean;
  lorArtifact: LorArtifactMock | null;
  generatedLors: LorArtifactMock[];
  finalizeDetails: FinalizeDetails;
  hasUnsavedFinalizeChanges: boolean;
  coverageType: CoverageType;
  coverageReviewed: boolean;
  callFailedPersisted: boolean;
  setCoverageType: (t: CoverageType) => void;
  markReviewed: (fieldKey: string) => void;
  modifyField: (fieldKey: string, value: string) => void;
  updateFinalizeDetail: <K extends keyof FinalizeDetails>(
    key: K,
    value: FinalizeDetails[K],
  ) => void;
  saveFinalizeDetails: () => void;
  triggerCall: () => void;
  generateLor: () => void;
  /** Map of requirement id to full requirement, for evidence pane */
  requirementsById: Record<string, DataRequirement>;
  /** Approve a specific value for a given requirement */
  confirmRequirementValue: (
    requirementId: string,
    valueId: string,
  ) => Promise<void>;
}

export const useClaimFilingState = ({
  matterId,
  coverageId,
}: UseClaimFilingStateArgs): UseClaimFilingState => {
  // Backend data
  const { data: allRequirements = [], refetch: refetchRequirements } =
    useGetMatterDataRequirementsQuery({ matterId });
  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  }, {
    refetchOnMountOrArgChange: true,
  });

  // Mutations for Accept/Modify
  const [approveValue] = useApproveDataRequirementValueMutation();
  const [createValue] = useCreateDataRequirementValueMutation();

  // Build helper maps
  const tasksById = useMemo(() => {
    const map = new Map<string, { id: string; coverage_id?: string | null }>();
    tasksResponse?.results?.forEach((t) =>
      map.set(t.id, { id: t.id, coverage_id: t.coverage_id }),
    );
    return map;
  }, [tasksResponse]);

  const requirementsById: Record<string, DataRequirement> = useMemo(() => {
    const rec: Record<string, DataRequirement> = {};
    allRequirements.forEach((r) => {
      rec[r.id] = r;
    });
    return rec;
  }, [allRequirements]);

  // Partition and transform into ReviewedFieldState
  const reviewedFields: ReviewedFieldState[] = useMemo(() => {
    if (!allRequirements?.length) return [];

    const fields: ReviewedFieldState[] = [];

    const isCoverageRequirementForCurrent = (req: DataRequirement): boolean => {
      const task = tasksById.get(req.task_id);
      return (
        req.canonical_model === 'insurance_coverage' &&
        task?.coverage_id === coverageId
      );
    };

    const isClientOrMatter = (req: DataRequirement): boolean => {
      return (
        req.canonical_model === 'client' || req.canonical_model === 'matter'
      );
    };

    const pushField = (
      req: DataRequirement,
      section: 'client' | 'coverage',
    ) => {
      // Choose the latest approved value (modified_at then created_at)
      const approved = (req.values || [])
        .filter((v) => v.status === DataRequirementValueStatus.APPROVED)
        .sort((a, b) =>
          (b.modified_at || b.created_at || '').localeCompare(
            a.modified_at || a.created_at || '',
          ),
        )[0];
      const extracted = req.values?.find(
        (v) => v.status === DataRequirementValueStatus.EXTRACTED,
      );
      // Display order: canonical > approved > extracted (so operators can see top suggestion)
      // Prefer canonical only if it is a non-empty string; otherwise fall back to approved/extracted
      const canonicalOrNull =
        typeof req.canonical_value === 'string' &&
        req.canonical_value.trim().length > 0
          ? req.canonical_value
          : null;
      const displayValue =
        canonicalOrNull ?? approved?.value ?? extracted?.value ?? null;
      // Only mark reviewed when canonical or an approved value exists
      const isReviewed = Boolean(req.canonical_value || approved);
      const required = req.status !== DataRequirementStatus.BYPASSED; // no bypass UI, but respect server state

      fields.push({
        key: req.id,
        displayName: req.display_name,
        required,
        value: displayValue,
        isReviewed,
        meta: { requirementId: req.id, taskId: req.task_id, section },
      });
    };

    // Maintain order: client/matter first, then coverage for this coverageId
    allRequirements.forEach((req) => {
      if (isClientOrMatter(req)) pushField(req, 'client');
    });
    allRequirements.forEach((req) => {
      if (isCoverageRequirementForCurrent(req)) pushField(req, 'coverage');
    });

    return fields;
  }, [allRequirements, tasksById, coverageId]);
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [isCallOutcomeReady, setIsCallOutcomeReady] = useState<boolean>(false);
  const [lorArtifact] = useState<LorArtifactMock | null>(null);
  const [generatedLors] = useState<LorArtifactMock[]>([]);
  const [finalizeDetails, setFinalizeDetails] = useState<FinalizeDetails>({
    policyNumber: '',
    claimNumber: '',
    adjusterName: '',
    adjusterEmail: '',
    adjusterPhone: '',
    adjusterFax: '',
    preferredContactInstructions: '',
    adjusterContactChoice: null,
  });
  const [hasUnsavedFinalizeChanges, setHasUnsavedFinalizeChanges] =
    useState<boolean>(false);
  const [coverageType, setCoverageTypeState] = useState<CoverageType>('1P');
  const [coverageReviewed, setCoverageReviewed] = useState<boolean>(false);
  const [callFailedPersisted, setCallFailedPersisted] = useState<boolean>(
    () => getCoverageProgress(coverageId).callFailed,
  );
  const timeoutRef = useRef<number | null>(null);
  const prefilledFinalizeRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const allRequiredReviewed = useMemo(() => {
    const clientRequiredOk = reviewedFields
      .filter((f) => f.required && f.meta?.section === 'client')
      .every((f) => f.isReviewed || !!f.value);
    return clientRequiredOk && coverageReviewed;
  }, [reviewedFields, coverageReviewed]);

  // Pull sensible defaults for finalize details directly from coverage data.
  // Previously used useCoverageLoRFields (lor-context endpoint); now derives
  // scalar defaults from the standard coverage query.
  const { data: coverageData } = useGetCoverageQuery(
    { matterId, coverageId },
    { skip: !coverageId },
  );

  // Prefill finalize details from coverage data. Resets when coverageId changes.
  const prevCoverageIdRef = useRef<string>(coverageId);
  useEffect(() => {
    const coverageChanged = prevCoverageIdRef.current !== coverageId;
    if (coverageChanged) {
      prevCoverageIdRef.current = coverageId;
      prefilledFinalizeRef.current = false;
      setFinalizeDetails({
        policyNumber: '',
        claimNumber: '',
        adjusterName: '',
        adjusterEmail: '',
        adjusterPhone: '',
        adjusterFax: '',
        preferredContactInstructions: '',
        adjusterContactChoice: null,
      });
      setHasUnsavedFinalizeChanges(false);
    }

    if (prefilledFinalizeRef.current) return;
    if (!coverageData) return;

    const primaryAdj = (coverageData.adjusters as AdjusterDetail[] | null)?.[0];
    const adjName = primaryAdj?.name ?? '';
    const handles: ContactHandleSchema[] = Array.isArray(primaryAdj?.handles)
      ? (primaryAdj.handles as ContactHandleSchema[])
      : [];
    const { email: adjEmail, phone: adjPhone, fax: adjFax } =
      extractContactChannels(handles);
    const contactChoice = buildPreferredContactChoice(handles, coverageId);

    const policy = coverageData.policy_identifiers?.[0]?.identifier_value ?? '';
    const claim = coverageData.claims?.[0]?.claim_identifier ?? '';

    setFinalizeDetails((prev) => ({
      policyNumber: prev.policyNumber || policy,
      claimNumber: prev.claimNumber || claim,
      adjusterName: prev.adjusterName || adjName,
      adjusterEmail: prev.adjusterEmail || adjEmail,
      adjusterPhone: prev.adjusterPhone || adjPhone,
      adjusterFax: prev.adjusterFax || adjFax,
      preferredContactInstructions: prev.preferredContactInstructions || '',
      adjusterContactChoice: prev.adjusterContactChoice || contactChoice,
    }));
    prefilledFinalizeRef.current = true;
  }, [coverageData, coverageId]);

  useEffect(() => {
    const clientFieldsOk = reviewedFields
      .filter((f) => f.required && f.meta?.section === 'client')
      .every((f) => f.isReviewed);
    if (clientFieldsOk) {
      setCoverageProgress(coverageId, { clientReviewed: true });
    }
  }, [reviewedFields, coverageId]);

  const setCoverageType = useCallback((t: CoverageType) => {
    setCoverageTypeState(t);
    setCoverageReviewed(true);
  }, []);

  const markReviewed = useCallback(
    async (fieldKey: string) => {
      const req = requirementsById[fieldKey];
      if (!req) return;
      // Approve the first EXTRACTED value if present; otherwise no-op
      const extracted = req.values?.find(
        (v) => v.status === DataRequirementValueStatus.EXTRACTED,
      );
      if (!extracted) return;
      try {
        await approveValue({
          matterId,
          taskId: req.task_id,
          requirementId: req.id,
          valueId: extracted.id,
        }).unwrap();
        await refetchRequirements();
      } catch (err) {
        // Let caller surface toast if needed; keep silent here to avoid coupling
        // console.error('Approve failed', err);
      }
    },
    [approveValue, matterId, requirementsById, refetchRequirements],
  );

  const modifyField = useCallback(
    async (fieldKey: string, value: string) => {
      const req = requirementsById[fieldKey];
      if (!req) return;
      try {
        await createValue({
          matterId,
          taskId: req.task_id,
          requirementId: req.id,
          value: {
            data_requirement_id: req.id,
            value,
            status: DataRequirementValueStatus.APPROVED,
          },
        }).unwrap();
        await refetchRequirements();
      } catch (err) {
        // console.error('Create value failed', err);
      }
    },
    [createValue, matterId, requirementsById, refetchRequirements],
  );

  const confirmRequirementValue = useCallback(
    async (requirementId: string, valueId: string) => {
      const req = requirementsById[requirementId];
      if (!req) return;
      try {
        await approveValue({
          matterId,
          taskId: req.task_id,
          requirementId: req.id,
          valueId,
        }).unwrap();
        await refetchRequirements();
      } catch (err) {
        // Re-throw so caller can surface error to user
        throw err as unknown;
      }
    },
    [approveValue, matterId, requirementsById, refetchRequirements],
  );

  const updateFinalizeDetail = useCallback(
    <K extends keyof FinalizeDetails>(key: K, value: FinalizeDetails[K]) => {
      setFinalizeDetails((prev) => ({ ...prev, [key]: value }));
      setHasUnsavedFinalizeChanges(true);
    },
    [],
  );

  const saveFinalizeDetails = useCallback(() => {
    setHasUnsavedFinalizeChanges(false);
    setCoverageProgress(coverageId, { ack: true });
  }, [coverageId]);

  const triggerCall = useCallback(() => {
    if (!allRequiredReviewed) return;
    // Move immediately to in_progress; no local timers
    setIsCallOutcomeReady(true);
    setCallStatus('in_progress');
    setCoverageProgress(coverageId, { callFailed: false });
    setCallFailedPersisted(false);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [allRequiredReviewed, coverageId]);

  // Removed simulation handlers; real outcome comes from Bland call APIs

  // Deprecated mock generator; kept as no-op for API compatibility with parent props
  const generateLor = useCallback(() => {}, []);

  return {
    reviewedFields,
    allRequiredReviewed,
    callStatus,
    isCallOutcomeReady,
    lorArtifact,
    generatedLors,
    finalizeDetails,
    hasUnsavedFinalizeChanges,
    coverageType,
    coverageReviewed,
    callFailedPersisted,
    setCoverageType,
    markReviewed,
    modifyField,
    updateFinalizeDetail,
    saveFinalizeDetails,
    triggerCall,
    generateLor,
    requirementsById,
    confirmRequirementValue,
  };
};
